import { createTheme } from '@mui/material/styles';

import themeComponents from './theme.json';
import type { TColors, TFontFamily, TScreens } from './types';

export default function getTheme() {
  const {
    atpColorScheme,
    swaColorScheme,
    screens,
    fontFamily,
  }: { atpColorScheme: TColors; swaColorScheme: TColors; screens: Record<TScreens, string>; fontFamily: TFontFamily } =
    themeComponents;

  const colors: TColors = import.meta.env.VITE_PORTAL === 'swa' ? swaColorScheme : atpColorScheme;
  const nScreens: Record<TScreens, number> = {
    xs: 0,
    sm: 0,
    md: 0,
    lg: 0,
    xl: 0,
  };

  Object.keys(screens).forEach((s) => {
    nScreens[s as keyof typeof screens] = parseInt(screens[s as keyof typeof screens].replace('px', ''), 10);
  });

  const serifFontFamily = fontFamily.serif.join(',');
  const sansFontFamily = fontFamily.sans.join(',');

  return createTheme({
    breakpoints: {
      values: {
        ...nScreens,
      },
    },

    palette: {
      mode: 'light',
      ...colors,
    },

    typography: {
      fontFamily: [
        sansFontFamily,
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(','),

      h1: {
        fontFamily: serifFontFamily,
        fontWeight: 700,
        fontSize: '2.215rem',
        lineHeight: 40.8 / 34,
      },
      h2: {
        fontFamily: serifFontFamily,
        fontWeight: 300,
        fontSize: '2.215rem',
        lineHeight: 40.8 / 34,
        letterSpacing: '-0.03125rem',
      },

      h3: {
        fontFamily: serifFontFamily,
        fontWeight: 700,
        fontSize: '1.5rem',
        lineHeight: 28.8 / 24,
      },

      h4: {
        fontFamily: serifFontFamily,
        fontWeight: 700,
        fontSize: '1.25rem',
        lineHeight: 24 / 20,
        letterSpacing: '0.015625rem',
      },

      h5: {
        fontFamily: serifFontFamily,
        fontWeight: 400,
        fontSize: '1.125rem',
        lineHeight: 21.6 / 18,
      },

      h6: {
        fontFamily: serifFontFamily,
        fontWeight: 700,
        fontSize: '.875rem',
        lineHeight: 16.24 / 14,
        letterSpacing: '0.009375rem',
      },

      subtitle1: {
        fontFamily: sansFontFamily,
        fontWeight: 600,
        fontSize: '1rem',
        lineHeight: 28 / 16,
        letterSpacing: '0.009375rem',
      },

      subtitle2: {
        fontFamily: sansFontFamily,
        fontWeight: 500,
        fontSize: '0.875rem',
        lineHeight: 21.98 / 14,
        letterSpacing: '0.00625rem',
      },

      body1: {
        fontFamily: sansFontFamily,
        fontWeight: 400,
        fontSize: '1rem',
        lineHeight: '1.5rem',
        letterSpacing: '0.009375rem',
      },

      body2: {
        fontFamily: sansFontFamily,
        fontSize: '0.875rem',
        lineHeight: 16.24 / 14,
        letterSpacing: '0.009375rem',
      },

      caption: {
        fontFamily: sansFontFamily,
        fontSize: '0.75rem',
        lineHeight: 14 / 12,
        letterSpacing: '0.025rem',
      },

      overline: {
        fontFamily: sansFontFamily,
        fontSize: '0.75rem',
        fontWeight: 400,
        lineHeight: 14.4 / 12,
        letterSpacing: '0.0625rem',
        textTransform: 'uppercase',
      },
    },

    shadows: [
      'none',
      '0px 3.2px 7.2px 0px rgba(0, 0, 0, 0.133), 0px 0.6px 1.8px 0px rgba(0, 0, 0, 0.11)',
      '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
      '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
      '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
      '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
      '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
      '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
      '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
      '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
      '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
      '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
      '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
      '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
      '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
      '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
      '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
      '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
      '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
      '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
      '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
      '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
      '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
      '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
      '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)',
    ],

    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            textTransform: 'none',
            borderRadius: 4,
            whiteSpace: 'nowrap',
          },
          endIcon: {
            marginRight: 0,
          },
          startIcon: {
            marginLeft: 0,
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: { borderRadius: 4 },
        },
      },
    },
  });
}
